import type * as THREE from 'three';
import { BLOCK_CAPS, type SwarmBlock } from '@vrlearning/lesson-schema';
import { buildButterflies } from '../butterflies.js';
import { buildSchool } from '../fish.js';
import type { WorldBuilder } from '../world.js';
import type { StatefulBlock } from './features.js';

const hex = (colour: string): number => Number.parseInt(colour.slice(1), 16);

/** The swarm block (V2; from butterflies.ts and fish.ts): one instanced mesh in the air; `level` brings them or sends them off. */
export function buildSwarmBlock(
  world: WorldBuilder,
  block: SwarmBlock,
  name: string,
  /** `ownFrame: false`: the caller ticks it in its own feature frame (after the features set its level). */
  options: { ownFrame?: boolean } = {},
): StatefulBlock & { tick(elapsed: number): void; readonly mesh: THREE.InstancedMesh } {
  const count = Math.min(block.count, BLOCK_CAPS.swarm);
  const swarm =
    block.kind === 'vlinders'
      ? buildButterflies(
          {
            centre: block.centre,
            radius: block.radius,
            count,
            colours: block.colours.map(hex),
            seed: block.seed,
            height: block.height,
            speed: block.speed,
            size: block.size,
          },
          world.wind,
        )
      : buildSchool(
          {
            centre: block.centre,
            radius: block.radius,
            spread: block.spread,
            count,
            seed: block.seed,
            height: block.height,
            speed: block.speed,
            size: block.size,
            ...(block.colours.length >= 1
              ? { colours: [block.colours[0]!, block.colours[1] ?? block.colours[0]!] as const }
              : {}),
          },
          world.wind,
        );
  swarm.mesh.name = name;
  world.addAir(swarm.mesh);
  let level = block.level;
  const tick = (elapsed: number): void => swarm.update(elapsed, level);
  tick(0);
  if (options.ownFrame !== false) world.onFeatureFrame((_dt, elapsed) => tick(elapsed));
  return {
    mesh: swarm.mesh,
    tick,
    set(setting, value) {
      if (setting === 'level') level = value as number;
    },
  };
}
